import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromComponentDefinition from './component-definition.reducer';
import { ComponentDefinitionWithRelations } from '@rappider/rappider-sdk';

/**
 * 1. Feature Selector
 * Selects the top-level 'componentDefinition' state from the store.
 */
export const selectComponentDefinitionState = createFeatureSelector<fromComponentDefinition.State>(
  fromComponentDefinition.componentDefinitionFeatureKey
);

/**
 * 2. State Slice Selectors
 * These selectors get a specific property from the feature state.
 */
export const selectAllComponentDefinitions = createSelector(
  selectComponentDefinitionState,
  (state): ComponentDefinitionWithRelations[] => state.data || [] // Return empty array if data is null/undefined
);

export const selectIsComponentDefinitionsLoading = createSelector(
  selectComponentDefinitionState,
  (state): boolean => state.isComponentDefinitionsLoading
);

export const selectIsComponentDefinitionsLoaded = createSelector(
  selectComponentDefinitionState,
  (state): boolean => state.isComponentDefinitionsLoaded
);

export const selectComponentBrowserTitle = createSelector(
  selectComponentDefinitionState,
  (state) => state.title
);

/**
 * 3. Selector for a Single Component Definition by ID (Your Specific Request)
 *
 * This uses a more advanced and performant pattern. First, we create a
 * "dictionary" (or entity map) of the component definitions for quick lookups.
 * Then, we create a selector that can use this dictionary to find a specific
 * component by its ID instantly.
 */

// Step 3a: Create a dictionary of component definitions { id: componentDefinition }
export const selectComponentDefinitionEntities = createSelector(
  selectAllComponentDefinitions,
  (componentDefinitions) =>
    componentDefinitions.reduce(
      (entities: { [id: string]: ComponentDefinitionWithRelations }, componentDefinition) => ({
        ...entities,
        [componentDefinition.id]: componentDefinition,
      }),
      {}
    )
);

// Step 3b: Create the selector that takes an ID as a prop
export const selectComponentDefinitionById = (id: string) => createSelector(
  selectComponentDefinitionEntities,
  (entities) => entities[id] // O(1) lookup, very fast!
);
